import { Directionality } from '@angular/cdk/bidi';
import { BooleanInput, coerceBooleanProperty, NumberInput } from '@angular/cdk/coercion';
import { ViewportRuler } from '@angular/cdk/scrolling';
import type { AfterContentChecked, AfterContentInit, AfterViewInit, OnDestroy } from '@angular/core';
import { EventEmitter } from '@angular/core';
import { Output } from '@angular/core';
import { HostBinding } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  Inject,
  Input,
  NgZone,
  Optional,
  QueryList,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatTabLabelWrapperDirective } from '../tab-label-wrapper.directive';
import { ANIMATION_MODULE_TYPE } from '@angular/platform-browser/animations';
import { MatInkBarDirective } from '../ink-bar.directive';
import { Platform } from '@angular/cdk/platform';
import { MatPaginatedTabHeader } from '../paginated-tab-header';

/**
 * The header of the tab group which displays a list of all the tabs in the tab group. Includes
 * an ink bar that follows the currently selected tab. When the tabs list's width exceeds the
 * width of the header container, then arrows will be displayed to allow the user to scroll
 * left and right across the header.
 */
@Component({
  selector: 'app-mat-tab-header',
  templateUrl: 'tab-header.component.html',
  styleUrls: ['tab-header.component.scss'],
  encapsulation: ViewEncapsulation.None,
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Default,
})
export class MatTabHeaderComponent
  extends MatPaginatedTabHeader
  implements AfterContentChecked, AfterContentInit, AfterViewInit, OnDestroy
{
  @ContentChildren(MatTabLabelWrapperDirective, { descendants: false })
  _items: QueryList<MatTabLabelWrapperDirective>;

  @Output() override selectFocusedIndex = new EventEmitter<number>();
  @Output() override indexFocused = new EventEmitter<number>();

  @ViewChild(MatInkBarDirective, { static: true }) _inkBar: MatInkBarDirective;
  @ViewChild('tabListContainer', { static: true }) _tabListContainer: ElementRef;
  @ViewChild('tabList', { static: true }) _tabList: ElementRef;
  @ViewChild('tabListInner', { static: true }) _tabListInner: ElementRef;
  @ViewChild('nextPaginator') _nextPaginator: ElementRef<HTMLElement>;
  @ViewChild('previousPaginator') _previousPaginator: ElementRef<HTMLElement>;

  @HostBinding('class')
  readonly hostClass = 'mat-tab-header';

  @HostBinding('class.mat-tab-header-pagination-controls-enabled')
  get paginationControlsEnabled(): boolean {
    return this._showPaginationControls;
  }

  @HostBinding('class.mat-tab-header-rtl')
  get isRtl(): boolean {
    return this._getLayoutDirection() === 'rtl';
  }

  /** Whether the ripple effect is disabled or not. */
  @Input()
  get disableRipple(): boolean {
    return this._disableRipple;
  }
  set disableRipple(value: BooleanInput) {
    this._disableRipple = coerceBooleanProperty(value);
  }

  /** The index of the active tab. */
  @Input()
  override get selectedIndex(): number {
    return super.selectedIndex;
  }
  override set selectedIndex(value: NumberInput) {
    super.selectedIndex = value;
  }

  private _disableRipple: boolean = false;

  constructor(
    elementRef: ElementRef,
    changeDetectorRef: ChangeDetectorRef,
    viewportRuler: ViewportRuler,
    @Optional() dir: Directionality,
    ngZone: NgZone,
    platform: Platform,
    @Optional() @Inject(ANIMATION_MODULE_TYPE) animationMode?: string,
  ) {
    super(elementRef, changeDetectorRef, viewportRuler, dir, ngZone, platform, animationMode);
  }

  protected _itemSelected(event: KeyboardEvent) {
    event.preventDefault();
  }
}
